import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import { Settings, Upload, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { StudioConfigData } from "@production-hub/shared";

interface ConfigCardProps {
  config: {
    id: string;
    name: string;
    description: string | null;
    version: number;
    config_data: StudioConfigData;
    updated_at: string;
  };
  isActive: boolean;
  editHref: string;
  onLoadToBridge: () => void;
  onDelete: () => void;
  isLoading?: boolean;
}

export const ConfigCard = ({
  config,
  isActive,
  editHref,
  onLoadToBridge,
  onDelete,
  isLoading,
}: ConfigCardProps) => {
  const configData = config.config_data;

  return (
    <Card
      className={isActive ? "border-primary" : undefined}
      data-testid={`config-card-${config.id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base" data-testid="config-card-name">
              {config.name}
            </CardTitle>
            {config.description && (
              <p className="text-sm text-muted-foreground mt-1">
                {config.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline">v{config.version}</Badge>
            {isActive && <Badge variant="default">Active</Badge>}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {configData && (
          <div className="flex gap-4 text-sm text-muted-foreground mb-3">
            <span>{configData.layouts?.length ?? 0} layouts</span>
            <span>{configData.profiles?.length ?? 0} profiles</span>
          </div>
        )}

        <p className="text-xs text-muted-foreground mb-3">
          Updated{" "}
          {formatDistanceToNow(new Date(config.updated_at), {
            addSuffix: true,
          })}
        </p>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            asChild
            data-testid="config-edit-button"
          >
            <Link to={editHref}>
              <Settings className="h-4 w-4 mr-1" />
              Edit
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadToBridge}
            disabled={isLoading}
            data-testid="config-load-bridge-button"
          >
            <Upload className="h-4 w-4 mr-1" />
            Load to Bridge
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="text-destructive hover:text-destructive ml-auto"
            data-testid="config-delete-button"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
